import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Add,
    ArrowBack,
    ArrowDownward,
    Close,
    DeleteOutline,
    ExpandMore,
    FirstPage,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LastPage,
    UploadFile,
    FilterAlt,
} from "@mui/icons-material";
import {
    Box,
    Typography,
    Toolbar,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    TablePagination,
    Button,
    Modal,
    TextField,
    FormGroup,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Checkbox,
    LinearProgress,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import { jsonToCSV, readString } from "react-papaparse";
import { DatePicker } from "../components/DatePicker";
import { TimePicker } from "../components/TimePicker";
import { DateTimePickerField } from "../components/DateTimePickerField";

const formatDateFilter = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return { date: formatDateFilter(date), time: "00:00" };
};

const getDefaultEndDate = () => {
    return { date: formatDateFilter(new Date()), time: "23:59" };
};

const toDatetimeStr = (filter, endOfDay = false) => {
    if (!filter?.date) return "";
    const [day, month, year] = filter.date.split("/");
    const formattedDate = `${year}-${month}-${day}`;
    if (filter.time) {
        return `${formattedDate} ${filter.time}:${endOfDay ? "59" : "00"}`;
    }
    return `${formattedDate} ${endOfDay ? "23:59:59" : "00:00:00"}`;
};

const formatApiDatetime = (datetime) => {
    if (datetime instanceof Date) {
        const year = datetime.getFullYear();
        const month = String(datetime.getMonth() + 1).padStart(2, "0");
        const day = String(datetime.getDate()).padStart(2, "0");
        const hours = String(datetime.getHours()).padStart(2, "0");
        const minutes = String(datetime.getMinutes()).padStart(2, "0");

        datetime = `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    const [date, time] = datetime.split(" ");
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year} ${time}`;
};

const isTemplateVariableBlock = (block) =>
    block &&
    typeof block === "object" &&
    !Array.isArray(block) &&
    (Object.hasOwn(block, "header") ||
        Object.hasOwn(block, "body") ||
        Object.hasOwn(block, "buttons"));

const hasTemplateVariables = (variables) =>
    variables && Object.values(variables).some(isTemplateVariableBlock);

const buildVariableForm = (
    definitions,
    templates = [],
    savedVariables = null,
) => {
    const legacy = Array.isArray(definitions);
    const hasSavedTemplateBlocks = hasTemplateVariables(savedVariables);
    const savedTemplateBlocks = hasSavedTemplateBlocks
        ? Object.fromEntries(
              Object.entries(savedVariables).filter(([, block]) =>
                  isTemplateVariableBlock(block),
              ),
          )
        : {};
    const legacyBodyValues = savedVariables
        ? Object.fromEntries(
              Object.entries(savedVariables).filter(([, value]) =>
                  ["string", "number"].includes(typeof value),
              ),
          )
        : {};
    const values = legacy
        ? JSON.parse(JSON.stringify(savedVariables || {}))
        : JSON.parse(JSON.stringify(savedTemplateBlocks));
    const fields = [];
    const bodyFields = new Map();
    const labels = {
        text: "Texto",
        image: "Imagem",
        video: "Vídeo",
        document: "Documento",
        url: "Link dinâmico",
        copy_code: "Copiar código",
    };
    const addBody = (name, templateId = null) => {
        if (!bodyFields.has(name)) {
            const field = {
                key: JSON.stringify(["body", name]),
                section: "body",
                name,
                label: name,
                templateIds: [],
            };
            bodyFields.set(name, field);
            fields.push(field);
        }
        if (templateId !== null)
            bodyFields.get(name).templateIds.push(templateId);
    };
    if (Array.isArray(definitions)) {
        definitions.forEach((name) => {
            values[name] ??= "";
            addBody(name);
        });
    } else {
        const templatePositions = new Map(
            templates.map((template, index) => [
                template.id,
                template.campaignPosition ?? index + 1,
            ]),
        );
        Object.entries(definitions || {}).forEach(
            ([templateId, definition]) => {
                const name =
                    templates.find((template) => template.id === templateId)
                        ?.name || templateId;
                const position = templatePositions.get(templateId);
                const templateLabel = position
                    ? `Template ${position} — ${name}`
                    : `Template — ${name}`;
                if (!legacy) {
                    const savedBlock = values[templateId] || {};
                    const savedBody =
                        savedBlock.body &&
                        typeof savedBlock.body === "object" &&
                        !Array.isArray(savedBlock.body)
                            ? savedBlock.body
                            : {};
                    const savedButtons = Array.isArray(savedBlock.buttons)
                        ? savedBlock.buttons
                        : [];
                    values[templateId] = {
                        header: savedBlock.header ?? "",
                        body: { ...savedBody },
                        buttons: [...savedButtons],
                    };
                    if (definition.header) {
                        fields.push({
                            key: JSON.stringify([templateId, "header"]),
                            section: "header",
                            templateIds: [templateId],
                            label: `${templateLabel} — Cabeçalho: ${labels[definition.header].toLowerCase()}`,
                            type: definition.header,
                        });
                    }
                    definition.buttons.forEach((type, index) => {
                        values[templateId].buttons[index] ??= type ? "" : null;
                        if (type)
                            fields.push({
                                key: JSON.stringify([
                                    templateId,
                                    "buttons",
                                    index,
                                ]),
                                section: "buttons",
                                templateIds: [templateId],
                                index,
                                label: `${templateLabel} — Botão ${index + 1}: ${labels[type].toLowerCase()}`,
                                type,
                            });
                    });
                }
                definition.body.forEach((name) => {
                    if (legacy) {
                        values[name] ??= "";
                        addBody(name);
                    } else {
                        values[templateId].body[name] ??=
                            legacyBodyValues[name] ?? "";
                        addBody(name, templateId);
                    }
                });
            },
        );
    }
    return { values, fields };
};

const getFieldValue = (variables, field) => {
    const block = variables[field.templateIds[0]];
    if (field.section === "header") return block?.header ?? "";
    if (field.section === "buttons") return block?.buttons[field.index] ?? "";
    return (
        (field.templateIds.length
            ? block?.body[field.name]
            : variables[field.name]) ?? ""
    );
};

const updateFieldValue = (variables, field, value) => {
    const updated = JSON.parse(JSON.stringify(variables));
    if (!field.templateIds.length) updated[field.name] = value;
    field.templateIds.forEach((id) => {
        if (field.section === "header") updated[id].header = value;
        else if (field.section === "buttons")
            updated[id].buttons[field.index] = value;
        else updated[id].body[field.name] = value;
    });
    return updated;
};

const isEmptyVariable = (value) => value == null || String(value).trim() === "";

const buildCsvVariableForm = (meta) => {
    const form = buildVariableForm(
        meta.variableDefinitions ?? meta.variables,
        meta.templates,
    );
    const fields = form.fields.map((field) => {
        if (field.section === "body") {
            return {
                ...field,
                column: `variable_body: ${field.name}`,
                legacyColumn: `variable: ${field.name}`,
            };
        }
        const template = meta.templates?.find(
            (template) => template.id === field.templateIds[0],
        );
        if (!template)
            throw new Error(
                "Não foi possível identificar o template das colunas. Atualize a página e tente novamente.",
            );
        return {
            ...field,
            column:
                field.section === "header"
                    ? `variable_header: ${template.name}`
                    : `variable_button: ${template.name}: ${field.index + 1}`,
        };
    });
    if (new Set(fields.map((field) => field.column)).size !== fields.length) {
        throw new Error(
            "Existem templates com nomes iguais gerando a mesma coluna no CSV. Revise as variações da campanha.",
        );
    }
    return { values: form.values, fields };
};

const readCsvVariables = (row, form) => {
    let variables = JSON.parse(JSON.stringify(form.values));
    const errors = [];
    form.fields.forEach((field) => {
        const hasNew = Object.hasOwn(row, field.column);
        const hasOld =
            field.legacyColumn && Object.hasOwn(row, field.legacyColumn);
        if (hasNew && hasOld && row[field.column] !== row[field.legacyColumn]) {
            errors.push(
                `colunas "${field.column}" e "${field.legacyColumn}" possuem valores diferentes`,
            );
            return;
        }
        const value = hasNew
            ? row[field.column]
            : hasOld
              ? row[field.legacyColumn]
              : "";
        if (isEmptyVariable(value)) {
            errors.push(`variável "${field.column}" não preenchida`);
        } else {
            variables = updateFieldValue(variables, field, String(value));
        }
    });
    return { variables, errors };
};

const replacePreviewVariables = (text, values) =>
    (text || "").replace(
        /\{\{(\w+)\}\}|\{(\w+)\}/g,
        (placeholder, named, simple) => values[named || simple] || placeholder,
    );

const getTemplatePreview = (template, variables) => {
    const values = hasTemplateVariables(variables)
        ? variables[template.id] || { header: null, body: {}, buttons: [] }
        : { header: null, body: variables, buttons: [] };
    const components = template.components || [];
    const header = components.find((component) => component.type === "HEADER");
    const body = components.find((component) => component.type === "BODY");
    const footer = components.find((component) => component.type === "FOOTER");
    const buttons = components.find(
        (component) => component.type === "BUTTONS",
    );
    return {
        header: header
            ? {
                  type: header.format.toLowerCase(),
                  text: (header.text || "").replace(
                      /\{\{\w+\}\}/g,
                      (placeholder) => values.header || placeholder,
                  ),
                  url: values.header || "",
              }
            : null,
        body: replacePreviewVariables(body?.text, values.body),
        footer: footer?.text || "",
        buttons: (buttons?.buttons || []).map((button, index) => ({
            text: button.text || "Copiar código",
            type: button.type,
            value:
                button.type === "URL"
                    ? (button.url || "").replace(
                          /\{\{\w+\}\}/g,
                          (placeholder) => values.buttons[index] || placeholder,
                      )
                    : button.type === "COPY_CODE"
                      ? values.buttons[index] || "Código não preenchido"
                      : button.type === "PHONE_NUMBER"
                        ? button.phone_number
                        : "",
        })),
    };
};

const getPreviewMediaUrl = (value) => {
    try {
        const url = new URL(value);
        return ["http:", "https:"].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

const TemplateMediaPreview = ({ type, value }) => {
    const [failed, setFailed] = useState(false);
    const url = getPreviewMediaUrl(value);
    const label = { image: "Imagem", video: "Vídeo", document: "Documento" }[
        type
    ];
    if (!url || failed) {
        return (
            <Box
                sx={{ p: 2, mb: 1, border: "1px dashed #aaa", borderRadius: 1 }}
            >
                <Typography>{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {failed
                        ? "Não foi possível carregar a prévia. Isso não impede salvar o registro."
                        : "Informe uma URL HTTP ou HTTPS para visualizar a mídia."}
                </Typography>
            </Box>
        );
    }
    if (type === "document") {
        return (
            <Box sx={{ p: 2, mb: 1, bgcolor: "#fff", borderRadius: 1 }}>
                <Typography>Documento</Typography>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {value}
                </Typography>
                <Button
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Abrir documento
                </Button>
            </Box>
        );
    }
    return type === "image" ? (
        <Box
            component="img"
            src={url}
            alt="Imagem do cabeçalho"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            sx={{
                display: "block",
                width: "100%",
                maxHeight: 280,
                objectFit: "contain",
                mb: 1,
                borderRadius: 1,
            }}
        />
    ) : (
        <Box
            component="video"
            src={url}
            controls
            preload="none"
            onError={() => setFailed(true)}
            sx={{
                display: "block",
                width: "100%",
                maxHeight: 280,
                mb: 1,
                borderRadius: 1,
            }}
        />
    );
};

const TemplatePreview = ({ template, variables }) => {
    const preview = getTemplatePreview(template, variables);
    return (
        <>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
            >
                {template.name}
            </Typography>
            {preview.header?.type === "text" && (
                <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                    {preview.header.text}
                </Typography>
            )}
            {["image", "video", "document"].includes(preview.header?.type) && (
                <TemplateMediaPreview
                    key={`${preview.header.type}:${preview.header.url}`}
                    type={preview.header.type}
                    value={preview.header.url}
                />
            )}
            <Typography
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
                {preview.body}
            </Typography>
            {preview.footer && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    {preview.footer}
                </Typography>
            )}
            {preview.buttons.map((button, index) => (
                <Box
                    key={index}
                    sx={{
                        borderTop: "1px solid #d4dcd4",
                        mt: 1,
                        pt: 1,
                        textAlign: "center",
                        overflowWrap: "anywhere",
                    }}
                >
                    <Typography color="primary">{button.text}</Typography>
                    {button.value && (
                        <Typography variant="caption" color="text.secondary">
                            {button.value}
                        </Typography>
                    )}
                </Box>
            ))}
        </>
    );
};

const CampaignEntriesPage = () => {
    const { campaignId } = useParams();

    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;
    const [meta, setMeta] = useState(null);
    const [entries, setEntries] = useState(null);

    const [open, setOpen] = useState(false);

    const [validate, setValidate] = useState(false);
    const [entryId, setEntryId] = useState(null);
    const [entryStatus, setEntryStatus] = useState(null);
    const [contact, setContact] = useState("");
    const [sendImmediately, setSendImmediately] = useState(false);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [variables, setVariables] = useState({});
    const [variableFields, setVariableFields] = useState([]);
    const [formContents, setFormContents] = useState([]);
    const [saveError, setSaveError] = useState("");
    const missingVariables = variableFields.some((field) =>
        isEmptyVariable(getFieldValue(variables, field)),
    );
    const [context, setContext] = useState("");

    const isNew = entryId === null;
    const isEditable =
        isNew || ["scheduled", "rescheduled"].some((e) => e === entryStatus);

    const [isContactImportOpen, setIsContactImportOpen] = useState(false);
    const [importStatus, setImportStatus] = useState(null);
    const [ignoreImportErrors, setIgnoreImportErrors] = useState(false);
    const [importProgress, setImportProgress] = useState(null);
    const contactImportFileRef = useRef(null);

    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(null);
    const rowsPerPage = 8;
    const offset = page * rowsPerPage;

    const [contactIdFilter, setContactIdFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [startDatetimeFilter, setStartDatetimeFilter] =
        useState(getDefaultStartDate);
    const [endDatetimeFilter, setEndDatetimeFilter] =
        useState(getDefaultEndDate);

    const [appliedContactIdFilter, setAppliedContactIdFilter] = useState("");
    const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
    const [appliedStartDatetimeFilter, setAppliedStartDatetimeFilter] =
        useState(() => {
            const d = getDefaultStartDate();
            const [day, month, year] = d.date.split("/");
            return `${year}-${month}-${day} ${d.time}:00`;
        });
    const [appliedEndDatetimeFilter, setAppliedEndDatetimeFilter] = useState(
        () => {
            const d = getDefaultEndDate();
            const [day, month, year] = d.date.split("/");
            return `${year}-${month}-${day} ${d.time}:59`;
        },
    );

    const isFilterApplied =
        appliedContactIdFilter !== "" ||
        appliedStatusFilter !== "" ||
        appliedStartDatetimeFilter !== "" ||
        appliedEndDatetimeFilter !== "";

    const dateRegex = useMemo(() => /^\d{2}\/\d{2}\/\d{4}$/, []);
    const timeRegex = useMemo(() => /^\d{2}:\d{2}$/, []);
    const phoneNumberRegex = useMemo(() => /^\+?\d{1,3}\d{2,}$/, []);
    const emailRegex = useMemo(() => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, []);

    const statusColor = {
        scheduled: {
            background: "rgb(3, 89, 214)",
            text: "white",
        },
        rescheduled: {
            background: "rgb(3, 89, 214)",
            text: "white",
        },
        queued: {
            background: "orange",
            text: "white",
        },
        processing: {
            background: "yellow",
            text: "inherit",
        },
        completed: {
            background: "green",
            text: "white",
        },
        failed: {
            background: "red",
            text: "white",
        },
        cancelled: {
            background: "gray",
            text: "white",
        },
    };

    const statusPtbr = {
        scheduled: "Agendado",
        rescheduled: "Reagendado",
        queued: "Na fila",
        processing: "Processando",
        completed: "Finalizado",
        failed: "Falhou",
        cancelled: "Cancelado",
    };

    const channelTypePtbr = {
        phone_number: "Telefone",
        email: "E-mail",
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const handleOpen = useCallback(
        (isNew = true) => {
            if (meta === null) return;

            if (isNew) {
                setEntryId(null);
                setEntryStatus(null);
                setContact("");
                setSendImmediately(false);
                setSelectedDate("");
                setSelectedTime("");
                const form = buildVariableForm(
                    meta.variableDefinitions ?? meta.variables,
                    meta.templates,
                );
                setVariables(form.values);
                setVariableFields(form.fields);
                setFormContents(
                    meta.templates?.length
                        ? meta.templates.map((template) => ({
                              template,
                              templateId: template.id,
                              content:
                                  template.components
                                      .find(
                                          (component) =>
                                              component.type === "BODY",
                                      )
                                      ?.text.replace(/\{\{/g, "{")
                                      .replace(/\}\}/g, "}") || "",
                          }))
                        : meta.contents.map((content) => ({ content })),
                );
                setContext("");
            }
            setValidate(false);
            setSaveError("");
            setOpen(true);
        },
        [meta],
    );

    const handleEditEntry = useCallback(
        (id) => {
            const entry = entries.find((entry) => entry.id === id);
            const { status, contactId, datetime, variables, context } = entry;

            setEntryId(id);
            setEntryStatus(status);
            setContact(contactId);
            setSendImmediately(datetime === null);
            setSelectedDate(
                datetime === null
                    ? ""
                    : formatApiDatetime(datetime).split(" ")[0],
            );
            setSelectedTime(datetime === null ? "" : datetime.split(" ")[1]);
            const form = buildVariableForm(
                entry.variableDefinitions ??
                    meta.variableDefinitions ??
                    meta.variables ??
                    Object.keys(variables),
                [
                    ...(meta.templates || []),
                    ...(entry.template &&
                    !meta.templates?.some(
                        (template) => template.id === entry.template.id,
                    )
                        ? [{ ...entry.template, campaignPosition: null }]
                        : []),
                ],
                variables,
            );
            setVariables(form.values);
            setVariableFields(form.fields);
            setFormContents([
                {
                    content: entry.content,
                    templateId: entry.templateId,
                    template: entry.template,
                },
            ]);
            setContext(context || "");

            handleOpen(false);
        },
        [entries, handleOpen, meta],
    );

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const createEntry = useCallback(
        async (entryData, isBulk = false) => {
            const bulkSuffix = isBulk ? "bulk/" : "";
            const response = await fetch(
                `${backendEndpoint}/campaigns/${campaignId}/entries/${bulkSuffix}?public_id=true`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                    body: JSON.stringify(entryData),
                },
            );

            const json = await response.json();
            if (!response.ok) {
                throw new Error(
                    typeof json.detail === "string"
                        ? json.detail
                        : "Não foi possível salvar a entrada. Confira os campos preenchidos.",
                );
            }
            return json;
        },
        [backendEndpoint, campaignId, getCookieValue],
    );

    const updateEntry = useCallback(
        async (entryId, entryData) => {
            const response = await fetch(
                `${backendEndpoint}/campaigns/${campaignId}/entries/${entryId}/?public_id=true`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                    body: JSON.stringify(entryData),
                },
            );
            if (!response.ok) {
                const json = await response.json();
                throw new Error(
                    typeof json.detail === "string"
                        ? json.detail
                        : "Não foi possível salvar a entrada. Confira os campos preenchidos.",
                );
            }
        },
        [backendEndpoint, campaignId, getCookieValue],
    );

    const cancelEntry = useCallback(
        async (entryId) => {
            await fetch(
                `${backendEndpoint}/campaigns/${campaignId}/entries/${entryId}/?public_id=true`,
                {
                    method: "DELETE",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                },
            );
        },
        [backendEndpoint, campaignId, getCookieValue],
    );

    const handleCancelEntry = async (id) => {
        await cancelEntry(id);
        const newEntries = entries.map((entry) =>
            entry.id === id ? { ...entry, status: "cancelled" } : entry,
        );
        setEntries(newEntries);
        handleClose();
    };

    const handleSearch = useCallback(() => {
        setAppliedContactIdFilter(contactIdFilter);
        setAppliedStatusFilter(statusFilter);
        setAppliedStartDatetimeFilter(
            toDatetimeStr(startDatetimeFilter, false),
        );
        setAppliedEndDatetimeFilter(toDatetimeStr(endDatetimeFilter, true));

        setPage(0);
    }, [contactIdFilter, statusFilter, startDatetimeFilter, endDatetimeFilter]);

    const fetchEntries = useCallback(
        async ({
            contactId = "",
            status = "",
            startDatetime = "",
            endDatetime = "",
            pageOffset = 0,
        } = {}) => {
            setEntries(null);

            const response = await fetch(
                `${backendEndpoint}/campaigns/${campaignId}/entries/?public_id=true&meta=true&limit=${rowsPerPage}&offset=${pageOffset}${
                    contactId
                        ? `&contact_id=${encodeURIComponent(contactId)}`
                        : ""
                }${status ? `&status=${encodeURIComponent(status)}` : ""}${
                    startDatetime
                        ? `&start_datetime=${encodeURIComponent(startDatetime)}`
                        : ""
                }${
                    endDatetime
                        ? `&end_datetime=${encodeURIComponent(endDatetime)}`
                        : ""
                }`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                },
            );

            const { total, campaignEntries, campaignMeta } =
                await response.json();

            setTotal(total);
            setEntries(campaignEntries);
            setMeta(campaignMeta);
        },
        [backendEndpoint, campaignId, rowsPerPage, getCookieValue],
    );

    const handleClearFilters = useCallback(() => {
        const defaultStart = getDefaultStartDate();
        const defaultEnd = getDefaultEndDate();

        setContactIdFilter("");
        setStatusFilter("");
        setStartDatetimeFilter(defaultStart);
        setEndDatetimeFilter(defaultEnd);
        setAppliedContactIdFilter("");
        setAppliedStatusFilter("");
        setAppliedStartDatetimeFilter(toDatetimeStr(defaultStart));
        setAppliedEndDatetimeFilter(toDatetimeStr(defaultEnd, true));
        setPage(0);
        fetchEntries({
            startDatetime: toDatetimeStr(defaultStart),
            endDatetime: toDatetimeStr(defaultEnd, true),
            pageOffset: 0,
        });
    }, [fetchEntries]);

    const loadEntries = useCallback(async () => {
        await fetchEntries({
            contactId: appliedContactIdFilter,
            status: appliedStatusFilter,
            startDatetime: appliedStartDatetimeFilter,
            endDatetime: appliedEndDatetimeFilter,
            pageOffset: offset,
        });
    }, [
        fetchEntries,
        offset,
        appliedContactIdFilter,
        appliedStatusFilter,
        appliedStartDatetimeFilter,
        appliedEndDatetimeFilter,
    ]);

    const handleOk = useCallback(async () => {
        setValidate(true);
        setSaveError("");
        if (
            meta === null ||
            (meta.channelType === "phone_number" &&
                !phoneNumberRegex.test(contact)) ||
            (meta.channelType === "email" && !emailRegex.test(contact)) ||
            (!sendImmediately && !dateRegex.test(selectedDate)) ||
            (!sendImmediately && !timeRegex.test(selectedTime)) ||
            missingVariables
        )
            return;

        const newEntry = {
            contactId: contact,
            datetime: sendImmediately
                ? null
                : `${selectedDate.split("/").reverse().join("-")} ${selectedTime}`,
            variables,
            context: context === "" ? null : context,
        };
        try {
            if (isNew) await createEntry(newEntry);
            else await updateEntry(entryId, newEntry);
        } catch (error) {
            setSaveError(error.message || "Erro ao salvar entrada");
            return;
        }
        handleClose();
        await loadEntries();
    }, [
        meta,
        contact,
        sendImmediately,
        selectedDate,
        selectedTime,
        missingVariables,
        variables,
        context,
        isNew,
        entryId,
        phoneNumberRegex,
        emailRegex,
        dateRegex,
        timeRegex,
        createEntry,
        updateEntry,
        handleClose,
        loadEntries,
    ]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const replaceTemplateVariables = (template, variables, templateId) => {
        const body = hasTemplateVariables(variables)
            ? variables[templateId]?.body || {}
            : variables;
        return template.replace(/{\w+}/g, (placeholderWithDelimiters) => {
            const placeholderWithoutDelimiters =
                placeholderWithDelimiters.substring(
                    1,
                    placeholderWithDelimiters.length - 1,
                );
            const stringReplacement =
                body[placeholderWithoutDelimiters] || placeholderWithDelimiters;
            return stringReplacement;
        });
    };

    const handleOpenContactImport = () => {
        if (!meta) {
            return;
        }

        setImportProgress(null);
        setImportStatus(null);
        setIsContactImportOpen(true);
    };

    const handleCloseContactImport = useCallback(() => {
        setIsContactImportOpen(false);
    }, []);

    const handleDownloadCsvExample = () => {
        let form;
        try {
            form = buildCsvVariableForm(meta);
        } catch (error) {
            window.alert(error.message);
            return;
        }
        const csvHeaders = [
            meta?.channelType,
            "send_immediately",
            "date",
            "time",
            "context",
            ...form.fields.map((field) => field.column),
        ];

        const csvRows = [
            [
                meta?.channelType === "phone_number"
                    ? "+5511999999991"
                    : "email1@exemple.com",
                false,
                "01/01/1990",
                "08:00",
                "Context 1",
                ...form.fields.map(() => ""),
            ],
            [
                meta?.channelType === "phone_number"
                    ? "+5511999999992"
                    : "email2@exemple.com",
                false,
                "02/01/1990",
                "14:45",
                "Context 2",
                ...form.fields.map(() => ""),
            ],
            [
                meta?.channelType === "phone_number"
                    ? "+5511999999993"
                    : "email3@exemple.com",
                true,
                null,
                null,
                "Context 3",
                ...form.fields.map(() => ""),
            ],
        ];

        const csvData = csvRows.map((row) =>
            csvHeaders.reduce((accumulator, header, index) => {
                accumulator[header] = row[index];
                return accumulator;
            }, {}),
        );

        const fileName = `Importar Registros - Campanha ${meta?.name}.csv`;
        const csvString = jsonToCSV(csvData);

        const blob = new Blob([csvString], {
            type: "text/csv;charset=utf-8;",
        });

        const elem = document.createElement("a");
        elem.download = fileName;
        elem.href = URL.createObjectURL(blob);

        elem.click();
    };

    const handleReadEntriesFile = (e) => {
        const file = e.target.files[0];
        contactImportFileRef.current.value = "";

        if (
            !file ||
            (!file.name.toLowerCase().endsWith(".csv") &&
                file.type !== "text/csv")
        ) {
            return;
        }

        let form;
        try {
            form = buildCsvVariableForm(meta);
        } catch (error) {
            window.alert(error.message);
            return;
        }

        const reader = new FileReader();

        const maxNewEntries = 1000;
        reader.onload = async (e) => {
            const csvString = e.target.result;

            let idx = 0;
            let headers = [];
            let validNewEntries = [];
            let errors = [];
            readString(csvString, {
                worker: true,
                dynamicTyping: false,
                skipEmptyLines: "greedy",
                step: (row) => {
                    if (idx === 0) {
                        headers = row.data.map((header) => header.trim());
                        if (new Set(headers).size !== headers.length) {
                            errors.push(
                                "arquivo: existem colunas com nomes repetidos",
                            );
                        }
                    } else if (idx < maxNewEntries + 1) {
                        const rowData = Object.fromEntries(
                            headers.map((header, headerIdx) => [
                                header,
                                row.data[headerIdx],
                            ]),
                        );

                        const contact = String(rowData[meta?.channelType]);
                        const immediateValue = (
                            rowData["send_immediately"] || ""
                        )
                            .trim()
                            .toLowerCase();
                        const sendImmediately = ["true", "1"].includes(
                            immediateValue,
                        );
                        const selectedDate = rowData["date"];
                        const selectedTime = rowData["time"];
                        const context = rowData["context"];
                        const { variables, errors: variableErrors } =
                            readCsvVariables(rowData, form);

                        let error = false;
                        const csvLine = idx + 1;

                        if (new Set(headers).size !== headers.length)
                            error = true;
                        if (
                            row.errors.length > 0 ||
                            row.data.length !== headers.length
                        ) {
                            error = true;
                            errors.push(
                                `linha #${csvLine}: formato CSV inválido ou quantidade de colunas incorreta`,
                            );
                        }
                        if (
                            !["", "true", "false", "1", "0"].includes(
                                immediateValue,
                            )
                        ) {
                            error = true;
                            errors.push(
                                `linha #${csvLine}: send_immediately deve ser true ou false`,
                            );
                        }

                        if (
                            meta.channelType === "phone_number" &&
                            (contact === null ||
                                !phoneNumberRegex.test(contact))
                        ) {
                            error = true;
                            errors.push(`linha #${csvLine}: telefone inválido`);
                        }
                        if (
                            meta.channelType === "email" &&
                            (contact === null || !emailRegex.test(contact))
                        ) {
                            error = true;
                            errors.push(`linha #${csvLine}: e-mail inválido`);
                        }

                        if (
                            !sendImmediately &&
                            (selectedDate === null ||
                                !dateRegex.test(selectedDate))
                        ) {
                            error = true;
                            errors.push(`linha #${csvLine}: data inválida`);
                        }

                        if (
                            !sendImmediately &&
                            (selectedTime === null ||
                                !timeRegex.test(selectedTime))
                        ) {
                            error = true;
                            errors.push(`linha #${csvLine}: hora inválida`);
                        }

                        variableErrors.forEach((message) => {
                            error = true;
                            errors.push(`linha #${csvLine}: ${message}`);
                        });

                        if (!error) {
                            validNewEntries.push({
                                contactId: contact,
                                datetime: sendImmediately
                                    ? null
                                    : `${selectedDate.split("/").reverse().join("-")} ${selectedTime}`,
                                variables,
                                context: context === "" ? null : context,
                            });
                        }
                    }

                    idx++;
                },
                complete: () => {
                    const totalEntries = Math.max(0, idx - 1);
                    if (totalEntries === 0) {
                        errors.push("arquivo: nenhum registro encontrado");
                    }
                    if (totalEntries > maxNewEntries) {
                        errors.push(
                            `arquivo: mais do que ${maxNewEntries} registros encontrados, apenas os ${maxNewEntries} primeiros serão cadastrados.`,
                        );
                    }

                    const successEntries = validNewEntries.length;
                    const errorEntries =
                        Math.min(totalEntries, maxNewEntries) - successEntries;
                    const ignoredEntries = Math.max(
                        0,
                        totalEntries - maxNewEntries,
                    );
                    const importStatus = {
                        totalEntries,
                        successEntries,
                        errorEntries,
                        ignoredEntries,
                        errors,
                        entries: validNewEntries,
                    };

                    setIgnoreImportErrors(false);
                    setImportStatus(importStatus);
                },
            });
        };

        reader.readAsText(file);
    };

    const handleBulkImport = async () => {
        if (!importStatus?.entries.length) return;
        setImportProgress(0);

        const importEntries = importStatus.entries;
        try {
            await createEntry(importEntries, true);
        } catch (error) {
            setImportProgress(null);
            setIgnoreImportErrors(false);
            setImportStatus((previous) => ({
                ...previous,
                errors: [...previous.errors, `importação: ${error.message}`],
            }));
            return;
        }
        setImportProgress(100);
        window.location.reload();
    };

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    return (
        <Box sx={{ width: "100%" }}>
            <Toolbar sx={{ padding: "0 !important" }}>
                <Link
                    to="/campaigns"
                    style={{
                        textDecoration: "none",
                        color: "inherit",
                        borderRadius: 100,
                    }}
                >
                    <IconButton title="voltar" edge="end" aria-label="edit">
                        <ArrowBack />
                    </IconButton>
                </Link>
            </Toolbar>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "row",
                }}
            >
                <Box
                    variant="h4"
                    sx={{
                        width: "150",
                        color: "#333",
                        fontSize: "24px",
                        fontWeight: "500",
                    }}
                >
                    Fila da Campanha - {meta?.name}
                    <Box
                        variant="div"
                        sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            pr: "10px",
                        }}
                    >
                        Todas as mensagens que foram ou serão enviadas
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                    }}
                >
                    <Button
                        id="import-campaign-entries-contacts-btn"
                        variant="outlined"
                        startIcon={<ArrowDownward />}
                        onClick={() => handleOpenContactImport()}
                        sx={{
                            marginBottom: 2,
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mr: 2,
                        }}
                    >
                        Importar registros
                    </Button>
                    <Button
                        id="add-campaign-entry-btn"
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpen}
                        sx={{
                            marginBottom: 2,
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                        }}
                    >
                        Adicionar à fila
                    </Button>
                </Box>
            </Box>

            {(!entries && !total && !isFilterApplied) ||
            (entries !== null && entries.length === 0 && !isFilterApplied) ? (
                <Box sx={{ textAlign: "left", mt: 10 }}>
                    <Box sx={{ mb: 2 }}>
                        Nada por aqui por enquanto, o que acha de adicionar um
                        registro agora mesmo?
                    </Box>

                    <Link onClick={() => handleOpen()}>
                        Clique aqui para adicionar um registro
                    </Link>
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                            gap: 1.5,
                            mt: 3,
                            flexWrap: "wrap",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Data Início
                            </Box>
                            <DateTimePickerField
                                value={startDatetimeFilter}
                                onChange={setStartDatetimeFilter}
                                placeholder="Data início..."
                                defaultTime="00:00"
                            />
                        </Box>

                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Data Fim
                            </Box>
                            <DateTimePickerField
                                value={endDatetimeFilter}
                                onChange={setEndDatetimeFilter}
                                placeholder="Data fim..."
                                defaultTime="23:59"
                            />
                        </Box>

                        <Box sx={{ minWidth: 250, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Contato
                            </Box>
                            <TextField
                                placeholder="Buscar por contato..."
                                variant="outlined"
                                size="small"
                                value={contactIdFilter}
                                onChange={(e) =>
                                    setContactIdFilter(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                fullWidth
                                sx={{
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                }}
                            />
                        </Box>

                        <Box sx={{ minWidth: 150, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Status
                            </Box>
                            <FormControl
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: "white" }}
                            >
                                <Select
                                    value={statusFilter}
                                    onChange={(e) =>
                                        setStatusFilter(e.target.value)
                                    }
                                    displayEmpty
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {Object.entries(statusPtbr).map(
                                        ([key, label]) => (
                                            <MenuItem key={key} value={key}>
                                                {label}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<FilterAlt />}
                            onClick={handleSearch}
                            sx={{
                                borderRadius: "25px",
                                padding: "8px 16px 6px",
                                textTransform: "none",
                                mb: 0.2,
                            }}
                        >
                            Filtrar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutline />}
                            onClick={handleClearFilters}
                            disabled={!isFilterApplied}
                            sx={{
                                borderRadius: "25px",
                                padding: "8px 16px 6px",
                                textTransform: "none",
                                mb: 0.2,
                            }}
                        >
                            Limpar
                        </Button>
                    </Box>

                    <TableContainer
                        sx={{
                            position: "relative",
                            borderRadius: 1,

                            mt: 4,
                        }}
                    >
                        <Table
                            sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
                        >
                            <TableHead
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                    fontWeight: "500",
                                }}
                            >
                                <TableRow>
                                    <TableCell>
                                        {meta?.channelType
                                            ? channelTypePtbr[meta.channelType]
                                            : "Contato"}
                                    </TableCell>
                                    <TableCell>Mensagem</TableCell>
                                    <TableCell>
                                        Data e hora programada
                                    </TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "500",
                                }}
                            >
                                {entries === null ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : entries.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            sx={{
                                                textAlign: "center",
                                                py: 4,
                                                color: "#777",
                                            }}
                                        >
                                            Nenhuma mensagem encontrada para os
                                            filtros selecionados.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    entries.map((entry, index) => (
                                        <TableRow
                                            key={index}
                                            onClick={() =>
                                                handleEditEntry(entry.id)
                                            }
                                        >
                                            <TableCell>
                                                {entry.contactId}
                                            </TableCell>
                                            <TableCell
                                                title={
                                                    entry.content.startsWith(
                                                        "Invalid Template: ",
                                                    )
                                                        ? `Nome: ${entry.content.split(":")[1].trim()}\nID: ${entry.content.split(":")[2]}`
                                                        : null
                                                }
                                            >
                                                {entry.content.startsWith(
                                                    "Invalid Template: ",
                                                )
                                                    ? "Template inválido"
                                                    : replaceTemplateVariables(
                                                            entry.content,
                                                            entry.variables,
                                                            entry.templateId,
                                                        ).length > 80
                                                      ? replaceTemplateVariables(
                                                            entry.content,
                                                            entry.variables,
                                                            entry.templateId,
                                                        ).slice(0, 77) + "..."
                                                      : replaceTemplateVariables(
                                                            entry.content,
                                                            entry.variables,
                                                            entry.templateId,
                                                        )}
                                            </TableCell>
                                            <TableCell>
                                                {entry.datetime === null
                                                    ? `Imediato (${formatApiDatetime(entry.createdOn)})`
                                                    : formatApiDatetime(
                                                          entry.datetime,
                                                      )}
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        backgroundColor:
                                                            statusColor[
                                                                entry.status
                                                            ].background,
                                                        color: statusColor[
                                                            entry.status
                                                        ].text,
                                                        px: 2,
                                                        py: 0.5,
                                                        width: "fit-content",
                                                        borderRadius: 1000,
                                                    }}
                                                >
                                                    {statusPtbr[entry.status]}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            <TableFooter
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                }}
                            >
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[rowsPerPage]}
                                        count={total}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Entry modal */}
            <Modal
                open={open}
                // onClose={handleClose}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box
                    id="contents-modal-container"
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 800,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">
                            {isNew
                                ? "Novo registro"
                                : isEditable
                                  ? "Editar registro"
                                  : "Visualizar registro"}
                        </Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        <Box className="formLabel">
                            {channelTypePtbr[meta?.channelType]}*
                        </Box>
                        <TextField
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                            variant="outlined"
                            margin="dense"
                            placeholder={
                                meta?.channelType === "phone_number"
                                    ? "+5511999999999"
                                    : "email@exemplo.com"
                            }
                            error={
                                validate &&
                                ((meta.channelType === "phone_number" &&
                                    !phoneNumberRegex.test(contact)) ||
                                    (meta.channelType === "email" &&
                                        !emailRegex.test(contact)))
                                    ? true
                                    : false
                            }
                            helperText={
                                validate &&
                                ((meta.channelType === "phone_number" &&
                                    !phoneNumberRegex.test(contact)) ||
                                    (meta.channelType === "email" &&
                                        !emailRegex.test(contact)))
                                    ? "Preencha com um contato válido"
                                    : ""
                            }
                            className="inputs"
                            disabled={!isEditable}
                            slotProps={
                                !isEditable
                                    ? {
                                          htmlInput: {
                                              readOnly: true,
                                              disableUnderline: true,
                                          },
                                      }
                                    : null
                            }
                        />

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <FormGroup>
                                <FormControlLabel
                                    label="Enviar imediatamente"
                                    sx={{ mt: 2 }}
                                    control={
                                        <Switch
                                            checked={sendImmediately}
                                            onChange={(e) =>
                                                setSendImmediately(
                                                    e.target.checked,
                                                )
                                            }
                                            disabled={!isEditable}
                                            slotProps={
                                                !isEditable
                                                    ? {
                                                          htmlInput: {
                                                              readOnly: true,
                                                              disableUnderline: true,
                                                          },
                                                      }
                                                    : null
                                            }
                                        />
                                    }
                                />
                            </FormGroup>

                            {!sendImmediately && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Box className="formLabel">Data*</Box>
                                        <DatePicker
                                            selectedDate={selectedDate}
                                            onSelectDate={setSelectedDate}
                                            disabled={!isEditable}
                                            error={
                                                validate &&
                                                !sendImmediately &&
                                                selectedDate === ""
                                                    ? true
                                                    : false
                                            }
                                            helperText={
                                                validate &&
                                                !sendImmediately &&
                                                selectedDate === ""
                                                    ? "Preencha com uma data válida"
                                                    : ""
                                            }
                                        />
                                    </Box>

                                    <Box>
                                        <Box className="formLabel">Hora*</Box>
                                        <TimePicker
                                            selectedTime={selectedTime}
                                            onSelectTime={setSelectedTime}
                                            disabled={!isEditable}
                                            error={
                                                validate &&
                                                !sendImmediately &&
                                                selectedTime === ""
                                                    ? true
                                                    : false
                                            }
                                            helperText={
                                                validate &&
                                                !sendImmediately &&
                                                selectedTime === ""
                                                    ? "Preencha com uma hora válida"
                                                    : ""
                                            }
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Accordion
                            defaultExpanded={variableFields.length > 0}
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                "&::before": { display: "none" },
                                boxShadow: "none",
                                border: "1px solid",
                                borderColor:
                                    validate && missingVariables
                                        ? "#d32f2f"
                                        : "rgba(224, 224, 224, 1)",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="tools-advanced-settings-content"
                                id="tools-advanced-settings-header"
                            >
                                <Box
                                    className="formLabel"
                                    sx={{ margin: "0 !important" }}
                                >
                                    Variáveis*
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {variableFields.length === 0 ? (
                                    <Typography>
                                        Sem variáveis para preencher
                                    </Typography>
                                ) : (
                                    <Box>
                                        {[
                                            [
                                                "header",
                                                "Variáveis de cabeçalho",
                                            ],
                                            ["body", "Variáveis de corpo"],
                                            ["buttons", "Variáveis de botão"],
                                        ].map(([section, label]) => {
                                            const fields =
                                                variableFields.filter(
                                                    (field) =>
                                                        field.section ===
                                                        section,
                                                );
                                            if (!fields.length) return null;
                                            return (
                                                <Box
                                                    key={section}
                                                    sx={{ mb: 2 }}
                                                >
                                                    <Box className="formLabel">
                                                        {label}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 2,
                                                        }}
                                                    >
                                                        {fields.map((field) => {
                                                            const value =
                                                                getFieldValue(
                                                                    variables,
                                                                    field,
                                                                );
                                                            const error =
                                                                validate &&
                                                                isEmptyVariable(
                                                                    value,
                                                                );
                                                            return (
                                                                <TextField
                                                                    key={
                                                                        field.key
                                                                    }
                                                                    label={
                                                                        field.label
                                                                    }
                                                                    required
                                                                    value={
                                                                        value
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        setVariables(
                                                                            (
                                                                                previous,
                                                                            ) =>
                                                                                updateFieldValue(
                                                                                    previous,
                                                                                    field,
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                                ),
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        !isEditable
                                                                    }
                                                                    error={
                                                                        error
                                                                    }
                                                                    helperText={
                                                                        error
                                                                            ? "Preencha este campo"
                                                                            : field.section ===
                                                                                    "header" &&
                                                                                field.type !==
                                                                                    "text"
                                                                              ? "Informe a URL da mídia"
                                                                              : field.type ===
                                                                                  "url"
                                                                                ? "Informe apenas o parâmetro variável do link"
                                                                                : ""
                                                                    }
                                                                    variant="outlined"
                                                                    margin="dense"
                                                                    className="inputs"
                                                                    sx={{
                                                                        flex:
                                                                            section ===
                                                                            "body"
                                                                                ? "1 1 240px"
                                                                                : "1 1 100%",
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                                <Box sx={{ mt: 4 }}>
                                    <Box className="formLabel">
                                        Visualizar mensagem
                                        {formContents.length > 1 ? "s" : ""}
                                    </Box>
                                    {formContents.map(
                                        (
                                            { content, templateId, template },
                                            index,
                                        ) => (
                                            <Box
                                                key={templateId || index}
                                                sx={{
                                                    backgroundColor: "#eaeeea",
                                                    p: 1.5,
                                                    mt: 1,
                                                    borderRadius: 1.5,
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {template ? (
                                                    <TemplatePreview
                                                        template={template}
                                                        variables={variables}
                                                    />
                                                ) : (
                                                    replaceTemplateVariables(
                                                        content,
                                                        variables,
                                                        templateId,
                                                    )
                                                )}
                                            </Box>
                                        ),
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                "&::before": { display: "none" },
                                boxShadow: "none",
                                border: "1px solid rgba(224, 224, 224, 1)",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="tools-advanced-settings-content"
                                id="tools-advanced-settings-header"
                            >
                                <Box
                                    className="formLabel"
                                    sx={{ margin: "0 !important" }}
                                >
                                    Avançado
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box className="formLabel">Contexto</Box>
                                <TextField
                                    placeholder="Adicione um contexto sobre o cliente ou sobre a campanha para que seu Agente de IA do Chiko possa prosseguir com o atendimento de forma ainda mais personalizada e assertiva"
                                    value={context}
                                    onChange={(e) => setContext(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    margin="dense"
                                    multiline
                                    minRows={5}
                                    maxRows={18}
                                    className="inputs"
                                    disabled={!isEditable}
                                    slotProps={
                                        !isEditable
                                            ? {
                                                  htmlInput: {
                                                      readOnly: true,
                                                      disableUnderline: true,
                                                  },
                                              }
                                            : null
                                    }
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 2,
                            }}
                        >
                            {saveError && (
                                <Typography color="error" sx={{ mt: 2 }}>
                                    {saveError}
                                </Typography>
                            )}
                            {isEditable && (
                                <>
                                    {!isNew && (
                                        <Button
                                            onClick={() =>
                                                handleCancelEntry(entryId)
                                            }
                                            variant="contained"
                                            sx={{
                                                marginTop: 2,
                                                backgroundColor: "#ee0000",
                                                color: "#ffffff",
                                            }}
                                        >
                                            Cancelar envio
                                        </Button>
                                    )}

                                    <Button
                                        id="create-content-btn"
                                        onClick={handleOk}
                                        variant="contained"
                                        sx={{
                                            marginTop: 2,
                                        }}
                                    >
                                        Salvar registro
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Contact Import Modal */}
            <Modal
                open={isContactImportOpen}
                // onClose={handleCloseContactImport}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box
                    id="contents-modal-container"
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 650,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">Importar registros</Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleCloseContactImport}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        {!importStatus ? (
                            <>
                                <Box>
                                    Importar registros através de um arquivo
                                    CSV.{" "}
                                    <span
                                        style={{
                                            color: "rgb(0, 0, 238)",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                        }}
                                        onClick={() =>
                                            handleDownloadCsvExample()
                                        }
                                    >
                                        Baixar um exemplo de CSV.
                                    </span>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        mt: 4,
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        className="formLabel"
                                        sx={{ mt: "0 !important" }}
                                    >
                                        Arquivo CSV:
                                    </Box>
                                    <Box>
                                        <input
                                            ref={contactImportFileRef}
                                            hidden
                                            type="file"
                                            accept=".csv"
                                            onChange={handleReadEntriesFile}
                                        />
                                        <Button
                                            id="import-entries-btn"
                                            variant="contained"
                                            startIcon={<UploadFile />}
                                            onClick={() =>
                                                contactImportFileRef.current.click()
                                            }
                                            sx={{
                                                borderRadius: "8px !important",
                                            }}
                                        >
                                            Escolher arquivo
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            //         {
                            //     totalEntries,
                            //     successEntries,
                            //     errorEntries,
                            //     ignoredEntries,
                            //     errors,
                            // }
                            <>
                                <Box>
                                    <Box className="formLabel">Status:</Box>
                                    <ul>
                                        <li>
                                            Total de entradas:{" "}
                                            {importStatus.totalEntries}
                                        </li>
                                        <li>
                                            Entradas a serem importadas:{" "}
                                            {importStatus.successEntries}
                                        </li>
                                        <li>
                                            Entradas com erros:{" "}
                                            {importStatus.errorEntries}
                                        </li>
                                        {importStatus.ignoredEntries > 0 && (
                                            <li>
                                                Entradas a serem ignoradas:{" "}
                                                {importStatus.ignoredEntries}
                                            </li>
                                        )}
                                    </ul>
                                </Box>

                                {importStatus.errors.length > 0 && (
                                    <Box>
                                        <Box className="formLabel">Erros:</Box>
                                        <ul
                                            style={{
                                                maxHeight: "280px",
                                                overflowY: "auto",
                                            }}
                                        >
                                            {importStatus.errors.map(
                                                (error) => (
                                                    <li>{error}</li>
                                                ),
                                            )}
                                        </ul>
                                    </Box>
                                )}

                                {importProgress === null ? (
                                    <>
                                        {importStatus.errors.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    gap: 2,
                                                    mt: 4,
                                                }}
                                            >
                                                <FormGroup>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    ignoreImportErrors
                                                                }
                                                                onChange={(e) =>
                                                                    setIgnoreImportErrors(
                                                                        e.target
                                                                            .checked,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Ignorar erros e importar mesmo assim"
                                                    />
                                                </FormGroup>
                                            </Box>
                                        )}

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                gap: 2,
                                            }}
                                        >
                                            <Button
                                                onClick={() =>
                                                    setImportStatus(null)
                                                }
                                                variant="contained"
                                                sx={{
                                                    marginTop: 2,
                                                    backgroundColor: "#777",
                                                    color: "#ffffff",
                                                }}
                                            >
                                                Voltar
                                            </Button>

                                            <Button
                                                disabled={
                                                    importStatus.entries
                                                        .length === 0 ||
                                                    (importStatus.errors
                                                        .length > 0 &&
                                                        !ignoreImportErrors)
                                                }
                                                onClick={() =>
                                                    handleBulkImport()
                                                }
                                                variant="contained"
                                                sx={{
                                                    marginTop: 2,
                                                }}
                                            >
                                                Importar registros
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            mt: 4,
                                            pr: 4,
                                            pl: 4,
                                        }}
                                    >
                                        <LinearProgressWithLabel
                                            variant="determinate"
                                            value={importProgress}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                <FirstPage />
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                <KeyboardArrowLeft />
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                <KeyboardArrowRight />
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                <LastPage />
            </IconButton>
        </Box>
    );
}

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                >{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}

export default CampaignEntriesPage;
